import { type CSSProperties, type PointerEvent, type WheelEvent, useEffect, useMemo, useRef, useState } from 'react'

import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'

import PriceDisplay from 'src/components/PriceDisplay'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import { pageBuilderOutlinedButtonSx, pageBuilderPurchaseButtonSx } from 'src/theme/pageBuilderButtons'
import { post } from 'src/functions/fetch'
import productUrl from 'src/functions/productUrl'
import { useDispatch } from 'src/redux/hooks'
import config from 'src/config'
import getProductImageUrl from 'src/pages/Product/functions/imageUrl'
import ShopLogo from 'template/logos/logo.svg'
import shopInfos from 'template/infos'
import {
  isPublicMagazineResponse,
  isPublicMagazinesResponse,
  publicMagazineResponse,
  publicMagazineSummary,
} from 'src/shared/apiBackendResponses/magazine'
import {
  MagazineImageCaptionPosition,
  MagazineImageLayout,
  MagazineOverlayPosition,
  MagazinePage,
  MagazineProductRef,
  MagazineTextPosition,
} from 'src/shared/dbTypes/magazine'
import { tProductPreview } from 'src/shared/frontendTypes/product'
import { tItem } from 'src/shared/frontendTypes/warenkorb'
import {
  createLandingPageImageAiGeneratedMatcher,
  mergeLandingPageImageAiGenerated,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

import useStyles from './styles'

interface Props {
  open: boolean
  onClose: () => void
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const MAGAZINE_PAGE_WIDTH = 980
const MAGAZINE_PAGE_HEIGHT = 620
const MAGAZINE_PAGE_GAP = 12

const getProductImage = (product: tProductPreview) => product.images?.[0]
const getProductTileImageUrl = (product: tProductPreview) => {
  const image = getProductImage(product)
  if (!image) return ''

  return getProductImageUrl(image, product.id, 'sm')
    || getProductImageUrl(image, product.id, 'xs')
    || getProductImageUrl(image, product.id, 'md')
    || ''
}
const getProductModalImageUrl = (product: tProductPreview) => {
  const image = getProductImage(product)
  if (!image) return ''

  return getProductImageUrl(image, product.id, 'lg')
    || getProductImageUrl(image, product.id, 'md')
    || getProductImageUrl(image, product.id, 'sm')
    || getProductImageUrl(image, product.id, 'xs')
    || ''
}

const normalizeMagazineImageUrl = (value?: string) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('/api/media/landingpages/')) return trimmed.replace('/api/media/landingpages/', '/api/images/landingpages/')
  if (trimmed.startsWith('/media/landingpages/')) return trimmed.replace('/media/landingpages/', '/api/images/landingpages/')
  if (trimmed.startsWith('/images/landingpages/')) return trimmed.replace('/images/landingpages/', '/api/images/landingpages/')
  return trimmed
}

const pageClassMap = {
  coverGrid: 'templateCoverGrid',
  editorialIndex: 'templateEditorialIndex',
  splitArticle: 'templateSplitArticle',
  imageEssay: 'templateImageEssay',
  heroFeature: 'templateHeroFeature',
  productEditorial: 'templateProductEditorial',
  productImage: 'templateProductImage',
  collage: 'templateCollage',
  pinboard: 'templatePinboard',
  faqText: 'templateFaqText',
} as const

const imageLayoutClassMap: Record<MagazineImageLayout, keyof ReturnType<typeof useStyles>> = {
  standard: 'imageStandard',
  wide: 'imageWide',
  fullscreen: 'imageFullscreen',
  hero: 'imageHero',
  portrait: 'imagePortrait',
  inset: 'imageInset',
  circle: 'imageCircle',
  cutout: 'imageCutout',
}

const templateImageLayoutSlots: Record<MagazinePage['template'], MagazineImageLayout[]> = {
  coverGrid: ['wide', 'standard', 'standard', 'standard'],
  editorialIndex: ['hero', 'inset'],
  splitArticle: ['portrait', 'standard'],
  imageEssay: ['fullscreen', 'inset'],
  heroFeature: ['fullscreen'],
  productEditorial: ['wide', 'inset'],
  productImage: ['wide'],
  collage: ['cutout', 'circle', 'portrait', 'standard'],
  pinboard: ['portrait', 'standard', 'wide', 'circle', 'inset'],
  faqText: [],
}

const getTemplateImageLayout = (
  template: MagazinePage['template'],
  index: number,
  fallback?: MagazineImageLayout,
) => templateImageLayoutSlots[template]?.[index] || fallback || 'standard'

const allowsImageFineTuning = (template: MagazinePage['template']) =>
  template === 'collage' || template === 'pinboard'

const captionPositionClassMap: Record<MagazineImageCaptionPosition, keyof ReturnType<typeof useStyles>> = {
  auto: 'captionAuto',
  insideBottom: 'captionAuto',
  insideTop: 'captionInsideTop',
  outside: 'captionOutside',
  hidden: 'captionHidden',
}

const overlayPositionClassMap: Record<MagazineOverlayPosition, keyof ReturnType<typeof useStyles>> = {
  topLeft: 'overlayTopLeft',
  topCenter: 'overlayTopCenter',
  topRight: 'overlayTopRight',
  middleLeft: 'overlayMiddleLeft',
  middleCenter: 'overlayMiddleCenter',
  middleRight: 'overlayMiddleRight',
  bottomLeft: 'overlayBottomLeft',
  bottomCenter: 'overlayBottomCenter',
  bottomRight: 'overlayBottomRight',
}

const textPositionClassMap: Record<MagazineTextPosition, keyof ReturnType<typeof useStyles>> = {
  auto: 'textAuto',
  topLeft: 'textTopLeft',
  topRight: 'textTopRight',
  center: 'textCenter',
  bottomLeft: 'textBottomLeft',
  bottomRight: 'textBottomRight',
  leftColumn: 'textLeftColumn',
  rightColumn: 'textRightColumn',
}

const magazineReturnStorageKey = 'dmok-magazine-return'
type MagazineTextField = 'kicker' | 'title' | 'subtitle' | 'body' | 'quote'

const getMagazineTextStyle = (page: MagazinePage, field: MagazineTextField): CSSProperties => {
  const style = page.textStyles?.[field]
  return {
    ...(style?.fontFamily ? { fontFamily: style.fontFamily } : {}),
    ...(style?.fontSize ? { fontSize: `${style.fontSize}px` } : {}),
    ...(style?.fontWeight ? { fontWeight: style.fontWeight } : {}),
    ...(style?.color ? { color: style.color } : {}),
  }
}

const getStaticImageSrc = (image: unknown) => {
  if (typeof image === 'string') return image
  if (image && typeof image === 'object' && 'src' in image) {
    const src = (image as { src?: unknown }).src
    return typeof src === 'string' ? src : ''
  }
  return ''
}

const shopLogoSrc = getStaticImageSrc(ShopLogo)

const getMagazineHashTarget = () => {
  if (typeof window === 'undefined') return {}

  const match = window.location.hash.match(/^#magazin(?::(\d+))?(?::(\d+))?$/i)
  if (!match) return {}

  const magazineId = Number(match[1])
  const pageIndex = Number(match[2])

  return {
    ...(Number.isInteger(magazineId) && magazineId > 0 ? { magazineId } : {}),
    ...(Number.isInteger(pageIndex) && pageIndex >= 0 ? { pageIndex } : {}),
  }
}

const getSpreadIndexForPage = (pageIndex: number, useTwoPageSpread: boolean) =>
  Math.max(0, useTwoPageSpread ? Math.floor(pageIndex / 2) : pageIndex)

const getPageIndexForSpread = (spreadIndex: number, useTwoPageSpread: boolean) =>
  Math.max(0, useTwoPageSpread ? spreadIndex * 2 : spreadIndex)

const MagazineModal: React.FC<Props> = ({ open, onClose, landingPageImageAiGenerated }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const stageRef = useRef<HTMLDivElement | null>(null)
  const twoPageSpreadRef = useRef(false)
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)

  const [loading, setLoading] = useState(false)
  const [magazines, setMagazines] = useState<publicMagazineSummary[]>([])
  const [activeMagazine, setActiveMagazine] = useState<publicMagazineResponse | null>(null)
  const [spreadIndex, setSpreadIndex] = useState(0)
  const [useTwoPageSpread, setUseTwoPageSpread] = useState(false)
  const [readerZoom, setReaderZoom] = useState(1)
  const [selectedProduct, setSelectedProduct] = useState<tProductPreview | null>(null)
  const [selectedProductEditorial, setSelectedProductEditorial] = useState<{
    title?: string
    subtitle?: string
    badge?: string
  } | null>(null)
  const [cartMessage, setCartMessage] = useState('')
  const [magazineResponseImageAiGenerated, setMagazineResponseImageAiGenerated] = useState<LandingPageImageAiGenerated | undefined>()
  const mergedLandingPageImageAiGenerated = useMemo(
    () => mergeLandingPageImageAiGenerated(
      landingPageImageAiGenerated,
      magazineResponseImageAiGenerated,
      activeMagazine?.landingPageImageAiGenerated,
    ),
    [activeMagazine?.landingPageImageAiGenerated, landingPageImageAiGenerated, magazineResponseImageAiGenerated]
  )
  const isAiGeneratedLandingImage = useMemo(
    () => createLandingPageImageAiGeneratedMatcher(mergedLandingPageImageAiGenerated),
    [mergedLandingPageImageAiGenerated]
  )

  const productsById = useMemo<Map<number, tProductPreview>>(() => new Map(
    (activeMagazine?.products || []).map(product => [product.id, product])
  ), [activeMagazine?.products])
  const productsByKey = useMemo<Map<string, tProductPreview>>(() => new Map(
    (activeMagazine?.products || []).map(product => [product.key, product])
  ), [activeMagazine?.products])
  const magazineMode = activeMagazine?.mode || 'magazine'
  const isProductImageMode = magazineMode === 'productImages'
  const isFaqMode = magazineMode === 'faq'

  const getProductForRef = (ref: { productId: number; productKey?: string }) =>
    (ref.productKey ? productsByKey.get(ref.productKey) : undefined)
    || productsById.get(ref.productId)

  const spreads = useMemo(() => {
    const pages = activeMagazine?.pages || []
    const visiblePages = isProductImageMode
      ? pages.filter(page => page.template === 'productImage')
      : pages
    if (isProductImageMode || isFaqMode || !useTwoPageSpread) return visiblePages.map(page => [page])

    const nextSpreads: MagazinePage[][] = []
    for (let index = 0; index < visiblePages.length; index += 2) {
      nextSpreads.push(visiblePages.slice(index, index + 2))
    }
    return nextSpreads
  }, [activeMagazine?.pages, isFaqMode, isProductImageMode, useTwoPageSpread])

  const useSpreadCounter = !isProductImageMode && useTwoPageSpread
  const pageCount = spreads.reduce((count, spread) => count + spread.length, 0)
  const activeFirstPageIndex = getPageIndexForSpread(spreadIndex, useSpreadCounter)
  const activeFirstPageNumber = Math.min(pageCount, activeFirstPageIndex + 1)
  const activeLastPageNumber = useSpreadCounter
    ? Math.min(pageCount, activeFirstPageIndex + spreads[spreadIndex]?.length)
    : activeFirstPageNumber
  const pageCounter = pageCount > 0
    ? activeFirstPageNumber === activeLastPageNumber
      ? `${activeFirstPageNumber} / ${pageCount}`
      : `${activeFirstPageNumber}-${activeLastPageNumber} / ${pageCount}`
    : '0 / 0'
  const activeSpreadLength = Math.max(1, spreads[spreadIndex]?.length || 1)
  const readerPageWidth = Math.round(MAGAZINE_PAGE_WIDTH * readerZoom)
  const readerTrackWidth = Math.round(
    readerPageWidth * activeSpreadLength
    + MAGAZINE_PAGE_GAP * Math.max(0, activeSpreadLength - 1)
  )
  const pageIndexById = useMemo(() => new Map(
    spreads.flat().map((page, index) => [page.id, index])
  ), [spreads])

  const selectProduct = (
    product: tProductPreview,
    editorial?: { title?: string; subtitle?: string; badge?: string },
  ) => {
    setSelectedProduct(product)
    setSelectedProductEditorial(editorial || null)
    setCartMessage('')
  }

  const closeProductModal = () => {
    setSelectedProduct(null)
    setSelectedProductEditorial(null)
  }

  const loadMagazines = async () => {
    setLoading(true)
    const res = await post({
      url: 'magazines',
      body: config.shop,
    })

    const nextMagazines = isPublicMagazinesResponse(res) ? res.magazines : []
    setMagazineResponseImageAiGenerated(
      isPublicMagazinesResponse(res) ? res.landingPageImageAiGenerated : undefined
    )
    setMagazines(nextMagazines)

    setLoading(false)
    return nextMagazines
  }

  const openMagazineById = async (magazineId: number, initialPageIndex = 0) => {
    setLoading(true)
    const res = await post({
      url: `magazines/${magazineId}`,
      body: config.shop,
    })

    if (isPublicMagazineResponse(res)) {
      setActiveMagazine(res)
      const visiblePages = res.mode === 'productImages'
        ? (res.pages || []).filter(page => page.template === 'productImage')
        : res.pages || []
      const useSpreadForResponse = (res.mode || 'magazine') === 'magazine' && useTwoPageSpread
      const maxSpreadIndex = Math.max(0, Math.ceil(visiblePages.length / (useSpreadForResponse ? 2 : 1)) - 1)
      setSpreadIndex(Math.max(0, Math.min(maxSpreadIndex, getSpreadIndexForPage(initialPageIndex, useSpreadForResponse))))
      closeProductModal()
    }

    setLoading(false)
  }

  const openMagazine = async (magazine: publicMagazineSummary) => {
    await openMagazineById(magazine.id, 0)
  }

  useEffect(() => {
    if (!open) return
    const target = getMagazineHashTarget()
    loadMagazines().then(() => {
      if (target.magazineId) openMagazineById(target.magazineId, target.pageIndex || 0)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open || !activeMagazine || isProductImageMode || typeof window === 'undefined') {
      setUseTwoPageSpread(false)
      return undefined
    }

    const updateMode = () => {
      const availableWidth = stageRef.current?.clientWidth || window.innerWidth
      setUseTwoPageSpread(availableWidth >= (MAGAZINE_PAGE_WIDTH * readerZoom * 2) + MAGAZINE_PAGE_GAP)
    }
    updateMode()

    const observer = typeof ResizeObserver !== 'undefined' && stageRef.current
      ? new ResizeObserver(updateMode)
      : null

    if (observer && stageRef.current) observer.observe(stageRef.current)
    window.addEventListener('resize', updateMode)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', updateMode)
    }
  }, [activeMagazine, isProductImageMode, open, readerZoom])

  useEffect(() => {
    if (!open || typeof document === 'undefined' || typeof window === 'undefined') return
    const scrollY = window.scrollY
    const body = document.body
    const html = document.documentElement
    const previousBodyStyles = {
      left: body.style.left,
      overflow: body.style.overflow,
      overscrollBehavior: body.style.overscrollBehavior,
      position: body.style.position,
      right: body.style.right,
      top: body.style.top,
      width: body.style.width,
    }
    const previousHtmlStyles = {
      overflow: html.style.overflow,
      overscrollBehavior: html.style.overscrollBehavior,
    }

    html.style.overflow = 'hidden'
    html.style.overscrollBehavior = 'none'
    body.style.left = '0'
    body.style.overflow = 'hidden'
    body.style.overscrollBehavior = 'none'
    body.style.position = 'fixed'
    body.style.right = '0'
    body.style.top = `-${scrollY}px`
    body.style.width = '100%'

    return () => {
      html.style.overflow = previousHtmlStyles.overflow
      html.style.overscrollBehavior = previousHtmlStyles.overscrollBehavior
      body.style.left = previousBodyStyles.left
      body.style.overflow = previousBodyStyles.overflow
      body.style.overscrollBehavior = previousBodyStyles.overscrollBehavior
      body.style.position = previousBodyStyles.position
      body.style.right = previousBodyStyles.right
      body.style.top = previousBodyStyles.top
      body.style.width = previousBodyStyles.width
      window.scrollTo(0, scrollY)
    }
  }, [open])

  useEffect(() => {
    if (spreadIndex >= spreads.length) setSpreadIndex(Math.max(0, spreads.length - 1))
  }, [spreadIndex, spreads.length])

  useEffect(() => {
    if (!activeMagazine) {
      twoPageSpreadRef.current = useTwoPageSpread
      return
    }

    const previousUseTwoPageSpread = twoPageSpreadRef.current
    if (previousUseTwoPageSpread === useTwoPageSpread) return

    setSpreadIndex(current => {
      const currentPageIndex = getPageIndexForSpread(current, previousUseTwoPageSpread)
      return Math.min(
        Math.max(0, spreads.length - 1),
        getSpreadIndexForPage(currentPageIndex, useTwoPageSpread),
      )
    })
    twoPageSpreadRef.current = useTwoPageSpread
  }, [activeMagazine, spreads.length, useTwoPageSpread])

  useEffect(() => {
    if (!activeMagazine || !stageRef.current) return
    const frame = window.requestAnimationFrame(() => {
      stageRef.current?.scrollTo({
        top: 0,
        behavior: 'auto',
      })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [activeMagazine?.id, spreadIndex])

  if (!open) return null

  const goToSpread = (index: number) => {
    const nextIndex = Math.max(0, Math.min(spreads.length - 1, index))
    setSpreadIndex(nextIndex)
    stageRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const changeReaderZoom = (direction: -1 | 1) => {
    setReaderZoom(current => Math.max(.72, Math.min(1.1, Number((current + direction * .06).toFixed(2)))))
  }

  const handleStagePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    pointerStartRef.current = {
      x: event.clientX,
      y: event.clientY,
    }
  }

  const handleStagePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current
    pointerStartRef.current = null
    if (!start) return

    const deltaX = event.clientX - start.x
    const deltaY = event.clientY - start.y
    if (Math.abs(deltaX) < 64 || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return

    goToSpread(spreadIndex + (deltaX < 0 ? 1 : -1))
  }

  const getStageWheelDeltaY = (event: WheelEvent<HTMLDivElement>) => {
    if (event.deltaMode === 1) return event.deltaY * 16
    if (event.deltaMode === 2) return event.deltaY * (stageRef.current?.clientHeight || 1)
    return event.deltaY
  }

  const handleStageWheel = (event: WheelEvent<HTMLDivElement>) => {
    const stage = stageRef.current
    if (!stage || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return

    const maxScrollTop = stage.scrollHeight - stage.clientHeight
    if (maxScrollTop <= 0) return

    const nextScrollTop = Math.max(0, Math.min(maxScrollTop, stage.scrollTop + getStageWheelDeltaY(event)))
    if (nextScrollTop === stage.scrollTop) return

    stage.scrollTop = nextScrollTop
    event.preventDefault()
    event.stopPropagation()
  }

  const closeReader = () => {
    if (activeMagazine) {
      setActiveMagazine(null)
      closeProductModal()
      setSpreadIndex(0)
      return
    }
    onClose()
  }

  const openProductPage = (product: tProductPreview) => {
    const articleUrl = productUrl(product.id, product.path, product.options)
    const pageIndex = getPageIndexForSpread(spreadIndex, useSpreadCounter)

    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(magazineReturnStorageKey, JSON.stringify({
        hash: activeMagazine ? `#magazin:${activeMagazine.id}:${pageIndex}` : '#magazin',
        page: pageIndex + 1,
        title: activeMagazine?.title || 'Magazin',
      }))
      window.location.href = articleUrl
      return
    }

    onClose()
  }

  const openFallbackProductPage = (productId: number, label?: string) => {
    if (!Number.isInteger(productId) || productId <= 0) return
    const articleUrl = productUrl(productId, label || `produkt-${productId}`)
    const pageIndex = getPageIndexForSpread(spreadIndex, useSpreadCounter)

    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(magazineReturnStorageKey, JSON.stringify({
        hash: activeMagazine ? `#magazin:${activeMagazine.id}:${pageIndex}` : '#magazin',
        page: pageIndex + 1,
        title: activeMagazine?.title || 'Magazin',
      }))
      window.location.href = articleUrl
    }
  }

  const getPageLogoSrc = (page: MagazinePage) => {
    if (!page.logo) return ''
    if (page.logo.source === 'none') return ''
    if (page.logo.source === 'shop') return shopLogoSrc
    return normalizeMagazineImageUrl(page.logo.src)
  }

  const addProductToCart = (product: tProductPreview) => {
    if (product.inquireOnly || product.purchaseMode === 'inquiry') return
    if (product.onlyStock && (product.stock || 0) <= 0) {
      setCartMessage('Dieser Artikel ist aktuell nicht verfügbar.')
      return
    }

    const price: tItem['price'] = product.discount
      ? {
        regularPrice: product.price,
        discountPrice: product.discount.price,
        discountPriceAfterStock: product.discount.priceAfterStock,
        stockDiscountLimit: product.discount.stockDiscountLimit,
        limitStockDiscountToStock: product.discount.limitStockDiscountToStock,
        quantityDiscount: product.discount.quantityDiscount,
        title: product.discount.metaTitle,
        type: product.discount.type,
        countdownVariant: product.discount.countdownVariant,
        dateEnd: product.discount.dateEnd,
        showPercent: product.discount.showPercent,
      }
      : product.price

    dispatch({
      type: 'addWarenkorbItem',
      payload: {
        id: product.id,
        categorie: product.categorie,
        key: product.key,
        hash: product.hash || product.key,
        path: product.path,
        name: product.name,
        brand: {
          id: product.brand,
          titel: product.brandTitel,
        },
        price,
        priceLock: product.priceLock,
        qty: 1,
        attributesText: product.attributes.map((attribute, index) => ({
          titel: `Variante ${index + 1}`,
          option: attribute,
        })),
        image: getProductImage(product),
        deliverytime: product.deliverytime,
        stockDeliverytime: product.stockDeliverytime,
        shippingType: product.shippingType,
        stock: product.stock,
        onlyStock: product.onlyStock,
        purchaseMode: product.purchaseMode,
        inquireOnly: product.inquireOnly,
      },
    })
    setCartMessage('Artikel wurde in den Warenkorb gelegt.')
  }

  const renderParagraphs = (text?: string) =>
    String(text || '')
      .split(/\r?\n/)
      .map(part => part.trim())
      .filter(Boolean)
      .map((part, index) => <p key={index}>{part}</p>)

  const renderProductTile = (ref: MagazineProductRef, index: number) => {
    const product = getProductForRef(ref)
    if (!product) return null
    const imageUrl = getProductTileImageUrl(product)
    const showPrice = ref.showPrice !== false

    return <button
      key={`${ref.productId}-${index}`}
      className={classes.productTile}
      type='button'
      onClick={() => {
        selectProduct(product, {
          badge: ref.badge,
          subtitle: ref.subtitle,
          title: ref.title,
        })
      }}
    >
      {imageUrl && <span className={classes.productTileImage}>
        <img src={imageUrl} alt={product.metaTitle} />
        <AiGeneratedBadge aiGenerated={getProductImage(product)?.aiGenerated} size='compact' />
      </span>}
      {ref.badge && <span>{ref.badge}</span>}
      <strong>{ref.title || product.metaTitle}</strong>
      {ref.subtitle && <span>{ref.subtitle}</span>}
      {showPrice &&
        <PriceDisplay
          price={product.discount
            ? {
              regularPrice: product.price,
              discountPrice: product.discount.price,
              discountPriceAfterStock: product.discount.priceAfterStock,
              stockDiscountLimit: product.discount.stockDiscountLimit,
              limitStockDiscountToStock: product.discount.limitStockDiscountToStock,
              quantityDiscount: product.discount.quantityDiscount,
              title: product.discount.metaTitle,
              type: product.discount.type,
              countdownVariant: product.discount.countdownVariant,
              dateEnd: product.discount.dateEnd,
              showPercent: product.discount.showPercent,
            }
            : product.price}
          component='span'
          variant='body2'
          align='left'
          onlyStock={product.onlyStock}
          stock={product.stock}
        />
      }
    </button>
  }

  const renderImageHotspots = (image: MagazinePage['images'][number]) =>
    (image.hotspots || []).map((hotspot, hotspotIndex) => {
      const product = getProductForRef(hotspot)
      const title = hotspot.label || product?.metaTitle || `Produkt ${hotspot.productId || ''}`.trim()
      const isLinked = Boolean(product || (Number.isInteger(hotspot.productId) && hotspot.productId > 0))

      return <button
        key={hotspotIndex}
        type='button'
        className={classes.imageHotspot}
        style={{ left: `${hotspot.x ?? 50}%`, top: `${hotspot.y ?? 50}%` }}
        title={title}
        aria-disabled={!isLinked}
        onClick={() => {
          if (!isLinked) return
          if (product) {
            selectProduct(product, { title: hotspot.label })
            return
          }
          openFallbackProductPage(hotspot.productId, hotspot.label || title)
        }}
      >
        <span>{title}</span>
      </button>
    })

  const renderProductImagePage = (page: MagazinePage) => {
    const pageIndex = pageIndexById.get(page.id) || 0
    const image = (page.images || []).find(entry => entry.src)
      || (pageIndex === 0 && activeMagazine?.coverImage?.src
        ? { ...activeMagazine.coverImage, layout: activeMagazine.coverImage.layout || 'wide' }
        : undefined)

    return <article
      key={page.id}
      className={classes.productImageSlide}
      style={{
        ['--magazine-accent' as string]: page.accentColor || '#8f947b',
        ['--magazine-page-height' as string]: `${Math.round(MAGAZINE_PAGE_HEIGHT * Math.max(100, page.heightVh || 100) / 100 * readerZoom)}px`,
      } as CSSProperties}
    >
      {image?.src
        ? <figure className={classes.productImageStage}>
          <img src={normalizeMagazineImageUrl(image.src)} alt={image.alt || page.title || activeMagazine?.title || ''} />
          <AiGeneratedBadge
            aiGenerated={image.aiGenerated || isAiGeneratedLandingImage(image.src)}
            placement='top-right'
          />
          {renderImageHotspots(image)}
          {image.caption && image.captionPosition !== 'hidden' && <figcaption>{image.caption}</figcaption>}
        </figure>
        : <div className={classes.productImageEmpty}>Kein Bild hinterlegt.</div>
      }
      {(page.products || []).length > 0 &&
        <div className={classes.productImageProducts}>
          {(page.products || []).map(renderProductTile)}
        </div>
      }
    </article>
  }

  const renderPage = (page: MagazinePage) => {
    if (isProductImageMode) return renderProductImagePage(page)

    const pageIndex = pageIndexById.get(page.id) || 0
    const pageImages = (page.images || []).filter(image => image.src)
    const images = pageImages.length > 0
      ? pageImages
      : pageIndex === 0 && activeMagazine?.coverImage?.src
        ? [{ ...activeMagazine.coverImage, layout: activeMagazine.coverImage.layout || 'wide' }]
        : []
    const pageHeight = Math.max(100, page.heightVh || 100)
    const pageLogo = page.logo
    const pageLogoSrc = getPageLogoSrc(page)
    const pageStyle: CSSProperties & Record<'--magazine-accent' | '--magazine-page-height', string> = {
      '--magazine-accent': page.accentColor || '#8f947b',
      '--magazine-page-height': `${Math.round(MAGAZINE_PAGE_HEIGHT * pageHeight / 100 * readerZoom)}px`,
    }
    if (page.backgroundColor) pageStyle.backgroundColor = page.backgroundColor
    if (page.textColor) pageStyle.color = page.textColor
    const templateClass = classes[pageClassMap[page.template]]

    return <article
      key={page.id}
      className={[
        classes.page,
        templateClass,
        classes[textPositionClassMap[page.textPosition || 'auto']],
      ].join(' ')}
      style={pageStyle}
    >
      {page.kicker && <div className={classes.pageKicker} style={getMagazineTextStyle(page, 'kicker')}>{page.kicker}</div>}
      {page.title && <h2 className={classes.pageTitle} style={getMagazineTextStyle(page, 'title')}>{page.title}</h2>}
      {page.subtitle && <div className={classes.pageSubtitle} style={getMagazineTextStyle(page, 'subtitle')}>{page.subtitle}</div>}
      {page.body && <div className={classes.pageBody} style={getMagazineTextStyle(page, 'body')}>{renderParagraphs(page.body)}</div>}
      {page.quote && <blockquote style={getMagazineTextStyle(page, 'quote')}>{page.quote}</blockquote>}
      {pageLogoSrc && pageLogo &&
        <div
          className={[
            classes.pageMark,
            classes[overlayPositionClassMap[pageLogo.position || 'topRight']],
          ].join(' ')}
          style={{ ['--magazine-logo-width' as string]: `${pageLogo.width || 180}px` } as CSSProperties}
        >
          <img src={pageLogoSrc} alt={pageLogo.alt || shopInfos.name || activeMagazine?.title || ''} />
          <AiGeneratedBadge
            aiGenerated={pageLogo.aiGenerated || isAiGeneratedLandingImage(pageLogo.src)}
            size='compact'
            placement='top-left'
          />
        </div>
      }
      {page.textLine?.text &&
        <div
          className={[
            classes.pageTextLine,
            classes[overlayPositionClassMap[page.textLine.position || 'bottomLeft']],
          ].join(' ')}
        >
          {page.textLine.text}
        </div>
      }
      {images.length > 0 &&
        <div className={classes.imageGrid}>
          {images.map((image, index) => {
            const slotLayout = getTemplateImageLayout(page.template, index, image.layout || 'standard')
            const useFineTuning = allowsImageFineTuning(page.template)

            return <figure
              key={index}
              className={[
                classes[imageLayoutClassMap[slotLayout]],
                classes[captionPositionClassMap[image.captionPosition || 'auto']],
              ].join(' ')}
              style={{
                ['--magazine-image-overlap-x' as string]: `${useFineTuning ? image.overlapX || 0 : 0}px`,
                ['--magazine-image-overlap-y' as string]: `${useFineTuning ? image.overlapY || 0 : 0}px`,
                ['--magazine-image-scale' as string]: `${useFineTuning ? Math.max(20, image.scale || 100) / 100 : 1}`,
              } as CSSProperties}
            >
              <img src={normalizeMagazineImageUrl(image.src)} alt={image.alt || ''} />
              <AiGeneratedBadge
                aiGenerated={image.aiGenerated || isAiGeneratedLandingImage(image.src)}
                placement='top-right'
              />
              {renderImageHotspots(image)}
              {image.caption && image.captionPosition !== 'hidden' && <figcaption>{image.caption}</figcaption>}
            </figure>
          })}
        </div>
      }
      {(page.products || []).length > 0 &&
        <div className={classes.products}>
          {(page.products || []).map(renderProductTile)}
        </div>
      }
    </article>
  }

  const renderProductModal = () => {
    if (!selectedProduct) return null
    const imageUrl = getProductModalImageUrl(selectedProduct)
    const showCartButton = selectedProduct.purchaseMode !== 'inquiry' && !selectedProduct.inquireOnly
    const editorialTitle = selectedProductEditorial?.title || selectedProduct.metaTitle

    return <div className={classes.productModalBackdrop} onClick={closeProductModal}>
      <div className={classes.productModal} onClick={event => event.stopPropagation()}>
        <IconButton
          aria-label='Produkt schliessen'
          onClick={closeProductModal}
          sx={{ position: 'absolute', right: 8, top: 8, zIndex: 2 }}
        >
          <CloseIcon />
        </IconButton>
        <div className={classes.productModalImage}>
          {imageUrl && <img src={imageUrl} alt={selectedProduct.metaTitle} />}
          <AiGeneratedBadge aiGenerated={getProductImage(selectedProduct)?.aiGenerated} />
          {selectedProductEditorial?.badge && <span>{selectedProductEditorial.badge}</span>}
        </div>
        <div className={classes.productModalContent}>
          <span>{selectedProduct.brandTitel}</span>
          <h3>{editorialTitle}</h3>
          {selectedProductEditorial?.subtitle && <p>{selectedProductEditorial.subtitle}</p>}
          <PriceDisplay
            price={selectedProduct.discount
              ? {
                regularPrice: selectedProduct.price,
                discountPrice: selectedProduct.discount.price,
                discountPriceAfterStock: selectedProduct.discount.priceAfterStock,
                stockDiscountLimit: selectedProduct.discount.stockDiscountLimit,
                limitStockDiscountToStock: selectedProduct.discount.limitStockDiscountToStock,
                quantityDiscount: selectedProduct.discount.quantityDiscount,
                title: selectedProduct.discount.metaTitle,
                type: selectedProduct.discount.type,
                countdownVariant: selectedProduct.discount.countdownVariant,
                dateEnd: selectedProduct.discount.dateEnd,
                showPercent: selectedProduct.discount.showPercent,
              }
              : selectedProduct.price}
            component='div'
            variant='h5'
            align='left'
            onlyStock={selectedProduct.onlyStock}
            stock={selectedProduct.stock}
          />
          {selectedProduct.attributes.length > 0 &&
            <small>{selectedProduct.attributes.join(', ')}</small>
          }
          <div className={classes.productModalActions}>
            {showCartButton &&
              <Button
                variant='contained'
                startIcon={<ShoppingBagIcon />}
                sx={pageBuilderPurchaseButtonSx}
                onClick={() => addProductToCart(selectedProduct)}
              >
                In den Warenkorb
              </Button>
            }
            <Button
              variant='outlined'
              startIcon={<OpenInNewIcon />}
              sx={pageBuilderOutlinedButtonSx}
              onClick={() => openProductPage(selectedProduct)}
            >
              Zum Artikel
            </Button>
          </div>
          {cartMessage && <p>{cartMessage}</p>}
        </div>
      </div>
    </div>
  }

  return <div className={classes.root} role='dialog' aria-modal='true'>
    <div className={classes.shell}>
      <div className={classes.toolbar}>
        <Button variant='text' onClick={closeReader}>
          {activeMagazine ? 'Alle Magazine' : 'Schliessen'}
        </Button>
        <div className={classes.toolbarTitle}>
          <strong>{activeMagazine?.title || 'Magazin'}</strong>
          {activeMagazine
            ? <span>{pageCounter}</span>
            : <span>{magazines.length} Ausgaben</span>
          }
        </div>
        <IconButton aria-label='Magazin schliessen' onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      {loading &&
        <div className={classes.empty}>
          <CircularProgress />
        </div>
      }

      {!loading && !activeMagazine &&
        <div className={classes.overview}>
          {magazines.length > 0
            ? <div className={classes.overviewGrid}>
              {magazines.map(magazine =>
                <button
                  key={magazine.id}
                  type='button'
                  className={classes.overviewCard}
                  onClick={() => openMagazine(magazine)}
                >
                  <div className={classes.overviewCover}>
                    {magazine.coverImage?.src
                      ? <img src={normalizeMagazineImageUrl(magazine.coverImage.src)} alt={magazine.coverImage.alt || magazine.title} />
                      : <span>{magazine.title}</span>
                    }
                    <AiGeneratedBadge
                      aiGenerated={magazine.coverImage?.aiGenerated || isAiGeneratedLandingImage(magazine.coverImage?.src)}
                      size='compact'
                      placement='top-left'
                    />
                  </div>
                  <div className={classes.overviewInfo}>
                    {magazine.issue && <span>{magazine.issue}</span>}
                    <h2>{magazine.title}</h2>
                    {magazine.subtitle && <p>{magazine.subtitle}</p>}
                    {magazine.excerpt && <p>{magazine.excerpt}</p>}
                  </div>
                </button>
              )}
            </div>
            : <div className={classes.empty}>Aktuell ist kein Magazin veröffentlicht.</div>
          }
        </div>
      }

      {!loading && activeMagazine &&
        <div
          className={[
            classes.reader,
            isProductImageMode ? classes.readerProductImages : '',
          ].join(' ')}
          style={{
            ['--magazine-reader-zoom' as string]: readerZoom,
            ['--magazine-page-width' as string]: `${readerPageWidth}px`,
            ['--magazine-page-gap' as string]: `${MAGAZINE_PAGE_GAP}px`,
            ['--magazine-track-width' as string]: `${readerTrackWidth}px`,
          } as CSSProperties}
        >
          <IconButton
            className={[classes.navButton, classes.navButtonPrev].join(' ')}
            onClick={() => goToSpread(spreadIndex - 1)}
            disabled={spreadIndex === 0}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          <div
            className={classes.stage}
            ref={stageRef}
            onPointerDown={handleStagePointerDown}
            onPointerCancel={() => { pointerStartRef.current = null }}
            onPointerUp={handleStagePointerUp}
            onWheelCapture={handleStageWheel}
          >
            <div className={classes.track}>
              {spreads[spreadIndex] &&
                <div
                  key={spreads[spreadIndex].map(page => page.id).join('-')}
                  className={[
                    classes.spread,
                    spreads[spreadIndex].length > 1 ? classes.doubleSpread : '',
                    spreads[spreadIndex].length === 1 ? classes.singleSpread : '',
                  ].join(' ')}
                >
                  {spreads[spreadIndex].map(renderPage)}
                </div>
              }
            </div>
          </div>
          <IconButton
            className={[classes.navButton, classes.navButtonNext].join(' ')}
            onClick={() => goToSpread(spreadIndex + 1)}
            disabled={spreadIndex >= spreads.length - 1}
          >
            <ArrowForwardIosIcon />
          </IconButton>
          <div className={classes.zoomControls}>
            <Button variant='text' onClick={() => changeReaderZoom(-1)}>Kleiner</Button>
            <span>{Math.round(readerZoom * 100)}%</span>
            <Button variant='text' onClick={() => changeReaderZoom(1)}>Groesser</Button>
          </div>
        </div>
      }
    </div>
    {renderProductModal()}
  </div>
}

export default MagazineModal
